import { Link } from "react-router-dom";
import { Building2, Users2, Briefcase, Clock, IdCard, ShieldUser, ChevronRight } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";

const cards = [
  {
    to: "/masters/locations",
    testid: "master-card-locations",
    icon: Building2,
    tint: "orange",
    title: "Locations",
    desc: "Sites / branches that departments, shifts and people roll up into.",
  },
  {
    to: "/masters/departments",
    testid: "master-card-departments",
    icon: Users2,
    tint: "navy",
    title: "Departments",
    desc: "Grouping within a Location with a reference headcount capacity.",
  },
  {
    to: "/masters/job-roles",
    testid: "master-card-job-roles",
    icon: Briefcase,
    tint: "peach",
    title: "Job Roles",
    desc: "Operational designations and assignments per Location.",
  },
  {
    to: "/masters/shifts",
    testid: "master-card-shifts",
    icon: Clock,
    tint: "peach",
    title: "Shifts",
    desc: "Timings, grace period and status per Location.",
  },
  {
    to: "/masters/employees",
    testid: "master-card-employees",
    icon: IdCard,
    tint: "slate",
    title: "Employees",
    desc: "Roster with QR-ready Employee ID, salary and assignments.",
  },
  {
    to: "/masters/users",
    testid: "master-card-users",
    icon: ShieldUser,
    tint: "orange",
    title: "Users",
    desc: "Console logins with role and Location scope.",
  },
];

const tintStyles = {
  peach:  { bg: "var(--vision-peach)",             fg: "var(--vision-ink)"    },
  navy:   { bg: "rgba(0, 53, 158, 0.10)",          fg: "var(--vision-navy)"   },
  slate:  { bg: "rgba(52, 73, 94, 0.10)",          fg: "var(--vision-slate)"  },
  orange: { bg: "rgba(247, 144, 9, 0.14)",         fg: "var(--vision-orange)" },
};

const MastersHome = () => {
  const { isLocationAdmin } = useAuth();
  const visibleCards = cards.filter(
    (c) =>
      !(
        isLocationAdmin &&
        (c.testid === "master-card-locations" || c.testid === "master-card-users")
      )
  );

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="masters-home-page">
      <AppHeader />
      <PageShell
        eyebrow="Master data"
        title="Everything the app runs on."
        subtitle={
          isLocationAdmin
            ? "Manage Departments, Job Roles, Shifts, and Employees for your Location."
            : "Set up Locations first, then Departments, Shifts, Employees, and finally console Users."
        }
        testId="masters-home"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleCards.map(({ to, testid, icon: Icon, tint, title, desc }) => (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className="group flex h-full flex-col rounded-2xl border bg-white p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-card-lg"
              style={{ borderColor: "rgba(52,73,94,0.08)" }}
            >
              <div
                className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl"
                style={{ backgroundColor: tintStyles[tint].bg, color: tintStyles[tint].fg }}
              >
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="font-display text-lg font-bold text-vision-ink">{title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-vision-slate/70">{desc}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-vision-navy">
                Manage
                <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </PageShell>
    </div>
  );
};

export default MastersHome;
